import { toCustomizationFields } from './cart.utils'
import { useAddItem, useRemoveItem } from '@framework/cart'
import {
  FileURL,
  ProductVariant,
  TOnlineEditorConfiguration,
} from '../../types'
import { Cart } from '@commerce/types'
import isEmpty from 'lodash/isEmpty'
import { checkAndRetainReorderFieldIfNeeded } from './useUploaderToCart.utils'
import { escapeEmoji } from '@utils/bigcommerce/escapeEmojis'
import { generateCrossSellMetadataValue } from '@utils/crossSell'
import { METADATA_TAG } from '@utils/constants'
import { TDesignJourney } from '../../../../_types'

export default function useUploaderToCart() {
  const addItem = useAddItem()
  const removeItem = useRemoveItem()

  const addItemToCart = async ({
    cartContent,
    productCategorySlug,
    selection,
    filesUrls,
    itemId,
    edit,
    brief = null,
    onlineEditor = null,
    designJourney,
    qrCodeChoice = null,
  }: {
    cartContent: Cart
    productCategorySlug: string
    selection: ProductVariant
    filesUrls?: FileURL[]
    itemId?: string
    edit?: string
    brief?: Record<string, string | string[] | number>
    onlineEditor?: TOnlineEditorConfiguration
    designJourney: TDesignJourney
    qrCodeChoice?: string
  }) => {
    // Save config and add configId into customization as well
    const cartItemCustomization = await toCustomizationFields({
      productCategorySlug,
      selection,
      brief,
      onlineEditor,
      filesUrls,
      designJourney,
      qrCodeChoice,
    })

    const customSelectionOption = [
      {
        option_id: selection?.modifiersId, // Set customizations field value
        option_value: escapeEmoji(`${JSON.stringify(cartItemCustomization)}`),
      },
    ]

    const metadataValue = generateCrossSellMetadataValue(productCategorySlug)
    if (metadataValue) {
      customSelectionOption.push({
        option_id: selection.metadataModifierId,
        option_value: metadataValue,
      })
    }

    // Check the exist item in cart
    //  * Check options againt selection to see what is the different value
    //  * Also check if the fileUrls is different from the one in customization
    //  * If only quantity is change, keep the Reorder modifier from the existing to the new one
    //  * Remove the old one and add new one
    if (edit === 'true' && itemId && !isEmpty(cartContent)) {
      const existingItem = cartContent.lineItems.find(
        (item) => item.id === itemId
      )
      if (!isEmpty(existingItem)) {
        checkAndRetainReorderFieldIfNeeded(
          existingItem,
          selection,
          filesUrls,
          customSelectionOption
        )

        // Remove existing one
        await removeItem(existingItem)

        const metadata = existingItem.options.find(
          (option) => option.name.toLowerCase() === METADATA_TAG
        )
        if (!isEmpty(metadata)) {
          customSelectionOption.push({
            option_id: selection.metadataModifierId,
            option_value: metadata.value,
          })
        }
      }
    }

    const itemPayload = {
      productId: String(selection.productId),
      variantId: String(selection.variantId),
      optionSelections: customSelectionOption,
    }

    // GTM event is already tracked through useAddItem hook
    const newCart = await addItem(itemPayload)
    const newItems = newCart.lineItems

    const newCartItem = newItems.slice(0)[0]

    return newCartItem
  }

  return [addItemToCart]
}
